import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { InfoBox } from '../components/InfoBox';
import { CodeBlock } from '../components/CodeBlock';
export function SecurityPage() {
  return <DocPageLayout title="Security and Fraud Prevention" description="Protect your business and customers with industry-leading security features and fraud prevention tools." data-id="element-1876">
      <p className="text-gray-700 mb-6" data-id="element-1877">
        The gateway provides multiple layers of security to protect transactions
        and prevent fraud. These features work together to ensure that payments
        are processed safely while minimizing false declines.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1878">
        3D Secure Authentication
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1879">
        3D Secure (3DS) adds an additional authentication step during online
        payments, requiring the cardholder to verify their identity with their
        issuing bank. The gateway supports both 3DS1 and 3DS2 protocols.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-1880">
        <div className="p-5 border border-gray-200 rounded-lg" data-id="element-1881">
          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1882">3DS 1.0</h3>
          <p className="text-sm text-gray-600" data-id="element-1883">
            Legacy protocol with redirect-based authentication. Supported for
            backward compatibility.
          </p>
        </div>
        <div className="p-5 border border-gray-200 rounded-lg border-orange-200 bg-orange-50/30" data-id="element-1884">
          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1885">
            3DS 2.0{' '}
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full ml-2" data-id="element-1886">
              Recommended
            </span>
          </h3>
          <p className="text-sm text-gray-600" data-id="element-1887">
            Modern protocol with frictionless flow, risk-based authentication,
            and improved user experience.
          </p>
        </div>
      </div>

      <CodeBlock language="JSON" title="3DS Authentication Request" code={`{
  "apiOperation": "INITIATE_AUTHENTICATION",
  "authentication": {
    "acceptVersions": "3DS2",
    "channel": "PAYER_BROWSER",
    "purpose": "PAYMENT_TRANSACTION"
  },
  "order": {
    "amount": "100.00",
    "currency": "USD"
  },
  "session": {
    "id": "SESSION0002abcdef1234567890"
  }
}`} data-id="element-1888" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1889">
        Address Verification Service (AVS)
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1890">
        AVS compares the billing address provided by the cardholder with the
        address on file at the issuing bank. This helps detect potentially
        fraudulent transactions where the card details may have been stolen.
      </p>
      <div className="overflow-x-auto my-6" data-id="element-1891">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1892">
          <thead data-id="element-1893">
            <tr className="bg-gray-50" data-id="element-1894">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1895">
                AVS Code
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1896">
                Meaning
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1897">
                Recommendation
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-1898">
            <tr data-id="element-1899">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1900">Y</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1901">Address and ZIP match</td>
              <td className="px-4 py-3 text-green-600 font-medium" data-id="element-1902">Accept</td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1903">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1904">A</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1905">
                Address matches, ZIP does not
              </td>
              <td className="px-4 py-3 text-amber-600 font-medium" data-id="element-1906">Review</td>
            </tr>
            <tr data-id="element-1907">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1908">Z</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1909">
                ZIP matches, address does not
              </td>
              <td className="px-4 py-3 text-amber-600 font-medium" data-id="element-1910">Review</td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1911">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1912">N</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1913">
                Neither address nor ZIP match
              </td>
              <td className="px-4 py-3 text-red-600 font-medium" data-id="element-1914">Decline</td>
            </tr>
            <tr data-id="element-1915">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1916">U</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1917">
                Information unavailable
              </td>
              <td className="px-4 py-3 text-amber-600 font-medium" data-id="element-1918">Review</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1919">
        Card Security Code (CSC/CVV)
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1920">
        Requiring the card security code (the 3 or 4 digit number on the card)
        helps verify that the customer has physical possession of the card. The
        gateway validates the CSC with the issuing bank and returns a match
        result.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1921">
        Fraud Detection Rules
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1922">
        Configure custom fraud detection rules to automatically screen
        transactions based on criteria such as:
      </p>
      <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4" data-id="element-1923">
        <li data-id="element-1924">
          <strong data-id="element-1925">Velocity checks</strong> — Limit the number of transactions
          per card or IP address within a time period
        </li>
        <li data-id="element-1926">
          <strong data-id="element-1927">Amount thresholds</strong> — Flag transactions above or below
          specified amounts
        </li>
        <li data-id="element-1928">
          <strong data-id="element-1929">Geographic restrictions</strong> — Block or review
          transactions from specific countries
        </li>
        <li data-id="element-1930">
          <strong data-id="element-1931">BIN blocking</strong> — Restrict specific card BIN ranges
        </li>
        <li data-id="element-1932">
          <strong data-id="element-1933">IP blacklisting</strong> — Block known fraudulent IP addresses
        </li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1934">
        Risk Assessment Parameters
      </h2>
      <div className="overflow-x-auto my-6" data-id="element-1935">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1936">
          <thead data-id="element-1937">
            <tr className="bg-gray-50" data-id="element-1938">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1939">
                Parameter
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1940">
                Type
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1941">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-1942">
            <tr data-id="element-1943">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1944">risk.assessment</td>
              <td className="px-4 py-3" data-id="element-1945">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1946">
                Overall risk level: LOW, MEDIUM, HIGH
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1947">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1948">risk.score</td>
              <td className="px-4 py-3" data-id="element-1949">Integer</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1950">
                Numeric risk score from 0-100
              </td>
            </tr>
            <tr data-id="element-1951">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1952">risk.rule[n].name</td>
              <td className="px-4 py-3" data-id="element-1953">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1954">
                Name of the triggered fraud rule
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1955">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1956">
                risk.rule[n].recommendation
              </td>
              <td className="px-4 py-3" data-id="element-1957">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1958">
                Recommended action: ACCEPT, REVIEW, REJECT
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox type="warning" title="PCI Compliance" data-id="element-1959">
        Regardless of which security features you enable, you must maintain PCI
        DSS compliance appropriate for your integration type. Consult the PCI
        Security Standards Council for current requirements.
      </InfoBox>
    </DocPageLayout>;
}